// Module Détails Logement - Interface Admin
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiChevronLeft, FiEdit, FiMapPin, FiSquare, FiDollarSign,
  FiCalendar, FiHome, FiCheck, FiX, FiImage, FiTrash2, FiPlus, FiStar
} from 'react-icons/fi';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Housing, HousingPhoto } from '@/models';
import { HousingsServices } from '@/services/HousingServices';
import { API_MEDIA_URL } from '@/services/service';
import { HousingFormModal } from '@/components/admin/HousingFormModal';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text}60;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
    color: ${theme.colors.primary};
  }

  svg {
    font-size: 24px;
  }
`;

const HeaderInfo = styled.div``;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0;
`;

const EditButton = styled.button`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}DD;
  }
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  margin-left: ${theme.spacing.md};

  ${props => {
    switch (props.status) {
      case 'available':
        return `
          background: ${theme.colors.success}20;
          color: ${theme.colors.success};
        `;
      case 'attributed':
        return `
          background: ${theme.colors.info}20;
          color: ${theme.colors.info};
        `;
      case 'maintenance':
        return `
          background: ${theme.colors.warning}20;
          color: ${theme.colors.warning};
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text};
        `;
    }
  }}
`;

const PhotoGallery = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
`;

const PhotoCard = styled.div`
  position: relative;
  aspect-ratio: 4/3;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  background: ${theme.colors.surface};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    transform: scale(1.02);
    box-shadow: ${theme.shadows.md};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PhotoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.surface};
  color: ${theme.colors.text}40;
  font-size: 48px;
`;

const PhotoActions = styled.div`
  position: absolute;
  top: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  display: flex;
  gap: ${theme.spacing.xs};
  opacity: 0;
  transition: opacity ${theme.transitions.fast};

  ${PhotoCard}:hover & {
    opacity: 1;
  }
`;

const PhotoActionButton = styled.button<{ variant?: 'danger' | 'primary' }>`
  padding: ${theme.spacing.xs};
  background: white;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  color: ${props => props.variant === 'primary' ? theme.colors.warning : theme.colors.error};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${props => props.variant === 'primary' ? theme.colors.warning + '10' : theme.colors.error + '10'};
  }
`;

const MainBadge = styled.div`
  position: absolute;
  top: ${theme.spacing.sm};
  left: ${theme.spacing.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${theme.colors.warning};
  color: white;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  box-shadow: ${theme.shadows.sm};
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const AddPhotoCard = styled.div`
  aspect-ratio: 4/3;
  border: 2px dashed ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.text}60;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    background: ${theme.colors.primary}05;
  }

  svg {
    font-size: 32px;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
`;

const DetailCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.xl};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.lg};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const InfoLabel = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  svg {
    color: ${theme.colors.primary};
  }
`;

const Description = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  line-height: 1.6;
  margin: ${theme.spacing.md} 0 0 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${theme.spacing.md} 0 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing.sm};
`;

const FeatureItem = styled.li`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.success}10;
  border-left: 3px solid ${theme.colors.success};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sizes.sm};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  svg {
    color: ${theme.colors.success};
    flex-shrink: 0;
  }
`;

const ConditionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${theme.spacing.md} 0 0 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const ConditionItem = styled.li`
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  line-height: 1.5;

  &::before {
    content: '•';
    color: ${theme.colors.primary};
    font-weight: bold;
    margin-right: ${theme.spacing.sm};
  }
`;

const EnergyBadge = styled.div<{ grade: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};

  ${props => {
    const colors: Record<string, string> = {
      'A': '#00A550',
      'B': '#4DB848',
      'C': '#C2D500',
      'D': '#F7EA00',
      'E': '#FBAF17',
      'F': '#EE7F00',
      'G': '#E12A21'
    };
    const color = colors[props.grade] || theme.colors.surface;
    return `
      background: ${color}20;
      color: ${color};
      border: 2px solid ${color};
    `;
  }}
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const LoadingText = styled.div`
  font-size: ${theme.typography.sizes.lg};
  color: ${theme.colors.text}60;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.xl};
`;

const ErrorText = styled.div`
  font-size: ${theme.typography.sizes.lg};
  color: ${theme.colors.error};
  margin-bottom: ${theme.spacing.md};
`;

const RetryButton = styled.button`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}DD;
  }
`;

export const HousingDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [housing, setHousing] = useState<Housing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch housing details from API
  const fetchHousingDetails = async () => {
    if (!id) {
      setError('ID du logement manquant');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await HousingsServices.get(Number(id)) as any;
      setHousing(response.data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement du logement');
      console.error('Error fetching housing details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load housing on component mount
  useEffect(() => {
    fetchHousingDetails();
  }, [id]);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available': return 'Disponible';
      case 'attributed': return 'Attribué';
      case 'maintenance': return 'Maintenance';
      default: return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return <FiCheck />;
      case 'attributed': return <FiHome />;
      case 'maintenance': return <FiX />;
      default: return null;
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !id) return;

    try {
      setUploadingPhotos(true);
      const fileArray = Array.from(files);
      await HousingsServices.uploadPhotos(Number(id), fileArray);

      // Recharger les détails du logement pour afficher les nouvelles photos
      await fetchHousingDetails();

      alert(`${fileArray.length} photo(s) téléchargée(s) avec succès`);
    } catch (err: any) {
      console.error('Error uploading photos:', err);
      alert('Erreur lors du téléchargement des photos');
    } finally {
      setUploadingPhotos(false);
      // Réinitialiser l'input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoId: number) => {
    if (!window.confirm('Supprimer cette photo ?') || !id) return;

    try {
      await HousingsServices.deletePhoto(Number(id), photoId);
      await fetchHousingDetails();
      alert('Photo supprimée avec succès');
    } catch (err: any) {
      console.error('Error deleting photo:', err);
      alert('Erreur lors de la suppression de la photo');
    }
  };

  const handleSetMainPhoto = async (photoId: number) => {
    if (!id) return;

    try {
      await HousingsServices.setMainPhoto(Number(id), photoId);
      await fetchHousingDetails();
      alert('Photo principale mise à jour');
    } catch (err: any) {
      console.error('Error setting main photo:', err);
      alert('Erreur lors de la mise à jour de la photo principale');
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Show loading state
  if (loading) {
    return (
      <DashboardLayout>
        <Container>
          <LoadingContainer>
            <LoadingText>Chargement du logement...</LoadingText>
          </LoadingContainer>
        </Container>
      </DashboardLayout>
    );
  }

  // Show error state
  if (error || !housing) {
    return (
      <DashboardLayout>
        <Container>
          <ErrorContainer>
            <ErrorText>{error || 'Logement introuvable'}</ErrorText>
            <RetryButton onClick={fetchHousingDetails}>
              Réessayer
            </RetryButton>
          </ErrorContainer>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate('/admin/housing')}>
              <FiChevronLeft />
            </BackButton>
            <HeaderInfo>
              <Title>
                {housing.reference}
                <StatusBadge status={housing.status || 'available'}>
                  {getStatusIcon(housing.status || 'available')}
                  {getStatusLabel(housing.status || 'available')}
                </StatusBadge>
              </Title>
              <Subtitle>{housing.type} - {housing.city}</Subtitle>
            </HeaderInfo>
          </HeaderLeft>
          <EditButton onClick={() => setIsModalOpen(true)}>
            <FiEdit /> Modifier
          </EditButton>
        </Header>

        <PhotoGallery>
          <SectionTitle>
            <FiImage /> Photos ({housing.housing_photos?.length || housing.photos?.length || 0})
          </SectionTitle>
          <PhotoGrid>
            {housing.housing_photos && housing.housing_photos.length > 0 ? (
              housing.housing_photos.map((photo) => (
                <PhotoCard key={photo.id}>
                  {photo.is_main && (
                    <MainBadge>
                      <FiStar /> Principale
                    </MainBadge>
                  )}
                  {photo.photo_url || photo.photo ? (
                    <img
                      src={`${API_MEDIA_URL}${photo.photo_url || photo.photo}`}
                      alt={photo.caption || `Photo ${photo.id}`}
                    />
                  ) : (
                    <PhotoPlaceholder>
                      <FiImage />
                    </PhotoPlaceholder>
                  )}
                  <PhotoActions>
                    {!photo.is_main && photo.id && (
                      <PhotoActionButton
                        variant="primary"
                        onClick={() => handleSetMainPhoto(photo.id!)}
                        title="Définir comme photo principale"
                      >
                        <FiStar />
                      </PhotoActionButton>
                    )}
                    {photo.id && (
                      <PhotoActionButton
                        onClick={() => handleDeletePhoto(photo.id!)}
                        title="Supprimer"
                      >
                        <FiTrash2 />
                      </PhotoActionButton>
                    )}
                  </PhotoActions>
                </PhotoCard>
              ))
            ) : (
              housing.photos && housing.photos.length > 0 ? (
                housing.photos.map((photo, index) => (
                  <PhotoCard key={index}>
                    {typeof photo === 'string' && photo ? (
                      <img
                        src={`${API_MEDIA_URL}${photo}`}
                        alt={`Photo ${index + 1}`}
                      />
                    ) : (
                      <PhotoPlaceholder>
                        <FiImage />
                      </PhotoPlaceholder>
                    )}
                  </PhotoCard>
                ))
              ) : null
            )}
            <AddPhotoCard onClick={() => fileInputRef.current?.click()}>
              <FiPlus />
              <span>{uploadingPhotos ? 'Téléchargement...' : 'Ajouter des photos'}</span>
            </AddPhotoCard>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              disabled={uploadingPhotos}
            />
          </PhotoGrid>
        </PhotoGallery>

        <DetailsGrid>
          <DetailCard>
            <SectionTitle>
              <FiHome /> Informations Générales
            </SectionTitle>

            <InfoGrid>
              <InfoItem>
                <InfoLabel>Type</InfoLabel>
                <InfoValue>{housing.type}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Surface</InfoLabel>
                <InfoValue>
                  <FiSquare />
                  {housing.surface} m²
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Nombre de pièces</InfoLabel>
                <InfoValue>{housing.rooms} pièces</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Chambres</InfoLabel>
                <InfoValue>{housing.bedrooms}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Salles de bain</InfoLabel>
                <InfoValue>{housing.bathrooms}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Disponibilité</InfoLabel>
                <InfoValue>
                  <FiCalendar />
                  {housing.available_date ? new Date(housing.available_date).toLocaleDateString('fr-FR') : 'N/A'}
                </InfoValue>
              </InfoItem>
            </InfoGrid>

            {housing.description && <Description>{housing.description}</Description>}
          </DetailCard>

          <DetailCard>
            <SectionTitle>
              <FiMapPin /> Localisation
            </SectionTitle>

            <InfoItem>
              <InfoLabel>Adresse complète</InfoLabel>
              <InfoValue style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <div>{housing.address}</div>
                <div>{housing.postal_code} {housing.city}</div>
              </InfoValue>
            </InfoItem>
          </DetailCard>
        </DetailsGrid>

        <DetailsGrid>
          <DetailCard>
            <SectionTitle>
              <FiDollarSign /> Informations Financières
            </SectionTitle>

            <InfoGrid>
              <InfoItem>
                <InfoLabel>Loyer mensuel</InfoLabel>
                <InfoValue style={{ color: theme.colors.primary }}>
                  {housing.rent} FCFA
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Charges</InfoLabel>
                <InfoValue>
                  {housing.charges} FCFA
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Total mensuel</InfoLabel>
                <InfoValue style={{ fontSize: theme.typography.sizes.lg, color: theme.colors.success }}>
                  {parseFloat(String(housing?.rent??0)) + parseFloat(String(housing?.charges??0))} FCFA
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Dépôt de garantie</InfoLabel>
                <InfoValue>
                  {housing.deposit} FCFA
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </DetailCard>

          <DetailCard>
            <SectionTitle>
              🌍 Performance Énergétique
            </SectionTitle>

            <InfoGrid>
              <InfoItem>
                <InfoLabel>Classe Énergétique (DPE)</InfoLabel>
                <InfoValue>
                  <EnergyBadge grade={housing.energy_class || 'C'}>
                    {housing.energy_class || 'N/A'}
                  </EnergyBadge>
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Émissions GES</InfoLabel>
                <InfoValue>
                  <EnergyBadge grade={housing.ghg_class || 'C'}>
                    {housing.ghg_class || 'N/A'}
                  </EnergyBadge>
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </DetailCard>
        </DetailsGrid>

        {housing.features && housing.features.length > 0 && (
          <DetailCard>
            <SectionTitle>
              <FiCheck /> Équipements et Prestations
            </SectionTitle>
            <FeatureList>
              {housing.features.map((feature, index) => (
                <FeatureItem key={index}>
                  <FiCheck />
                  {feature}
                </FeatureItem>
              ))}
            </FeatureList>
          </DetailCard>
        )}

        {housing.conditions && housing.conditions.length > 0 && (
          <DetailCard>
            <SectionTitle>
              📋 Conditions de Location
            </SectionTitle>
            <ConditionList>
              {housing.conditions.map((condition, index) => (
                <ConditionItem key={index}>
                  {condition}
                </ConditionItem>
              ))}
            </ConditionList>
          </DetailCard>
        )}
      </Container>


      <HousingFormModal
      key={`housing-${housing?.id}`}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={() => {
                  fetchHousingDetails();

                }}
                initialData={housing}
                mode={'edit'}
              />
    </DashboardLayout>
  );
};
